/**
 * ToastHost — viewport único que renderiza los toasts del ToastBusService.
 *
 * core/ empuja toasts a un bus desacoplado del DS (interceptores, ErrorHandler,
 * realtime); este organism los pinta. Se monta UNA sola vez en el shell
 * autenticado (main-layout) para que cualquier feature que dispare un toast lo
 * muestre sin acoplar nada a shared/ui.
 *
 * El auto-dismiss lo agenda el propio bus (setTimeout con la duración por
 * severidad); acá solo reaccionamos a la señal `toasts`. Los errores quedan
 * pegajosos (durationMs=0) y requieren cierre manual.
 *
 * a11y: cada toast usa role=alert (assertive) para errores y role=status
 * (polite) para el resto. Las animaciones de entrada respetan
 * prefers-reduced-motion.
 */
import { ChangeDetectionStrategy, Component, inject } from '@angular/core';

import { ToastBusService, type ToastSeverity } from '@core/core.public-api';

import { IconComponent } from '../../atoms/icon/icon.component';

interface SeverityMeta {
  readonly container: string;
  readonly icon: string;
  readonly iconColor: string;
  readonly role: 'status' | 'alert';
  readonly live: 'polite' | 'assertive';
}

const SEVERITY_META: Record<ToastSeverity, SeverityMeta> = {
  success: {
    container: 'border-success/30 bg-success-tint',
    icon: 'check',
    iconColor: 'text-success',
    role: 'status',
    live: 'polite',
  },
  info: {
    container: 'border-info/30 bg-info-tint',
    icon: 'bell',
    iconColor: 'text-info',
    role: 'status',
    live: 'polite',
  },
  warning: {
    container: 'border-warning/30 bg-warning-tint',
    icon: 'alert-triangle',
    iconColor: 'text-warning',
    role: 'status',
    live: 'polite',
  },
  error: {
    container: 'border-danger/30 bg-danger-tint',
    icon: 'alert-triangle',
    iconColor: 'text-danger',
    role: 'alert',
    live: 'assertive',
  },
};

@Component({
  selector: 'pvp-toast',
  imports: [IconComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div
      class="pointer-events-none fixed inset-x-0 bottom-0 z-[60] flex flex-col items-center gap-2 p-4 sm:inset-x-auto sm:right-0 sm:w-96 sm:items-end"
    >
      @for (toast of bus.toasts(); track toast.id) {
        <div
          class="pvp-toast pointer-events-auto flex w-full max-w-sm items-start gap-3 rounded-md border p-4 shadow-overlay"
          [class]="meta(toast.severity).container"
          [attr.role]="meta(toast.severity).role"
          [attr.aria-live]="meta(toast.severity).live"
        >
          <span class="mt-0.5 shrink-0" [class]="meta(toast.severity).iconColor" aria-hidden="true">
            <pvp-icon [name]="meta(toast.severity).icon" size="sm" />
          </span>

          <div class="min-w-0 flex-1">
            <p class="m-0 text-sm font-semibold text-text">{{ toast.title }}</p>
            @if (toast.detail) {
              <p class="m-0 mt-0.5 text-sm text-text-muted">{{ toast.detail }}</p>
            }
          </div>

          <button
            type="button"
            class="shrink-0 rounded-sm p-1 text-text-muted transition-colors hover:bg-surface-raised hover:text-text focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
            aria-label="Cerrar"
            (click)="bus.dismiss(toast.id)"
          >
            <pvp-icon name="x" size="sm" />
          </button>
        </div>
      }
    </div>
  `,
  styles: [
    `
      /* Entrada suave del toast. prefers-reduced-motion desactiva el desplazamiento
         y deja el toast visible de inmediato (sin transición engañosa). */
      .pvp-toast {
        animation: pvp-toast-in 180ms ease-out;
      }

      @keyframes pvp-toast-in {
        from {
          opacity: 0;
          transform: translateY(0.5rem);
        }
        to {
          opacity: 1;
          transform: translateY(0);
        }
      }

      @media (prefers-reduced-motion: reduce) {
        .pvp-toast {
          animation: none;
        }
      }
    `,
  ],
})
export class ToastHostComponent {
  protected readonly bus = inject(ToastBusService);

  protected meta(severity: ToastSeverity): SeverityMeta {
    return SEVERITY_META[severity];
  }
}
